import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, ArrowUpRight, Timer, Users, Sparkles, RefreshCw, UserPlus, NotebookPen } from "lucide-react";
import { MaskedLines, Reveal, FadeIn } from "@/components/motion";
import Marquee from "@/components/Marquee";
import Spotlight from "@/components/Spotlight";
import StoreButtons from "@/components/StoreButtons";
import NotifyForm from "@/components/NotifyForm";
import PricingCards from "@/components/PricingCards";
import SectionHeading from "@/components/SectionHeading";
import { usePageMeta } from "@/lib/meta";

const FEATURES = [
  { icon: Timer, title: "AI Practice Plans", text: "Full practices built around your roster — timed stations, setup notes, and coaching points for every drill." },
  { icon: Users, title: "Field Lineup Builder", text: "Set all nine positions and your bench, inning by inning. AI Suggest handles the first draft." },
  { icon: Sparkles, title: "AI Batting Order", text: "An order built from your coaching notes, player development, and handedness — not a random draw." },
  { icon: RefreshCw, title: "Multiple Teams", text: "Fall ball, spring, travel, rec, softball — switch teams without losing last season." },
  { icon: UserPlus, title: "Assistant Coaches Included", text: "One subscription per team. Your assistants coach alongside you at no extra cost." },
  { icon: NotebookPen, title: "Player Development Notes", text: "Keep every observation in one place so the next practice picks up where the last one ended." },
];

const Home = () => {
  usePageMeta(
    "CoachER | Your Digital Dugout AI Assistant",
    "CoachER is the digital dugout for youth baseball & softball coaches — AI practice plans, lineups, batting orders, rosters, and player development in one place.",
    "/"
  );

  const heroRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const rotX = useTransform(sy, [0, 1], [5, -5]);
  const rotY = useTransform(sx, [0, 1], [-7, 7]);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const drift = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const onMouseMove = (e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        onMouseMove={onMouseMove}
        data-testid="home-hero"
        className="relative overflow-hidden bg-coacher-navy pt-32 pb-20 sm:pt-44 sm:pb-28"
      >
        <video
          data-testid="hero-bg-video"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
          poster="/assets/hero-loop.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
        >
          <source src="/assets/hero-loop.webm" type="video/webm" />
          <source src="/assets/hero-loop.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-coacher-navy/70" aria-hidden="true" />
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <span aria-hidden="true" className="pointer-events-none absolute -right-10 top-16 select-none font-varsity text-[22rem] leading-none text-white/[0.04] hidden lg:block">C</span>

        <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <FadeIn>
              <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-white/70">
                <span className="inline-block h-px w-10 bg-coacher-red" aria-hidden="true" />
                Your Digital Dugout AI Assistant
              </p>
            </FadeIn>
            <MaskedLines
              lines={["Less admin.", "More coaching."]}
              delay={0.15}
              className="mt-6 font-display text-5xl sm:text-7xl lg:text-8xl font-semibold uppercase leading-[0.95] tracking-tight text-white"
              lineClassName="[&:nth-child(2)]:text-outline-white"
            />
            <FadeIn delay={0.5}>
              <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-white/70">
                CoachER gives youth baseball and softball coaches one digital dugout — AI-assisted practice plans, field lineups, batting orders, rosters, and player development, all in one place.
              </p>
            </FadeIn>
            <FadeIn delay={0.65}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/pricing"
                  data-testid="hero-trial-cta"
                  className="inline-flex h-13 items-center gap-2 rounded-md bg-coacher-red px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-coacher-red-dark"
                >
                  Start Your Free Trial <ArrowRight size={16} />
                </Link>
                <Link
                  to="/features"
                  data-testid="hero-features-cta"
                  className="inline-flex items-center gap-2 rounded-md border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-white hover:bg-white/5"
                >
                  See What CoachER Can Do
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={0.8}>
              <StoreButtons className="mt-10" />
            </FadeIn>
          </div>

          {/* Parallax screenshot stack */}
          <motion.div style={{ y: drift }} className="relative mx-auto hidden w-full max-w-sm sm:block lg:max-w-none" data-testid="hero-screenshots">
            <motion.div style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 1000 }} className="relative">
              <motion.img
                src="/assets/app-lineup.jpeg"
                alt="CoachER field lineup builder"
                className="relative z-10 w-64 rounded-2xl border border-white/15 shadow-frame lg:w-72"
                initial={{ opacity: 0, y: 60, rotate: 2 }}
                animate={{ opacity: 1, y: 0, rotate: 2 }}
                transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              />
              <motion.img
                src="/assets/app-batting.jpeg"
                alt="CoachER AI batting order"
                className="absolute -right-4 top-24 w-56 rounded-2xl border border-white/15 shadow-frame lg:-right-8 lg:w-64"
                initial={{ opacity: 0, y: 90, rotate: -3 }}
                animate={{ opacity: 1, y: 0, rotate: -3 }}
                transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              />
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1.1 }}
                className="absolute -left-3 top-10 z-20 rounded-sm bg-coacher-blue px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow-lg"
              >
                AI Generated
              </motion.span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Marquee items={["Better prepared coaches", "Better experiences for players", "Built from the dugout", "One practice at a time", "Give coaches time back"]} />

      <Spotlight />

      {/* FEATURES GRID */}
      <section data-testid="home-features" className="bg-coacher-surface py-20 sm:py-28 pinstripes">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <SectionHeading
            eyebrow="Features"
            title="One dugout. Everything organized."
            sub="The administrative work of coaching — planning, lineups, notes, development — pulled into a single place that fits in your pocket."
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <div data-testid={`feature-card-${i}`} className="group h-full rounded-xl border border-coacher-border bg-white p-7 shadow-card transition-all duration-300 hover:-translate-y-1.5 hover:border-coacher-navy/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-coacher-navy transition-colors duration-300 group-hover:bg-coacher-red">
                    <f.icon size={20} className="text-white" strokeWidth={2} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold uppercase tracking-wide text-coacher-navy">{f.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-coacher-slate">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/features" data-testid="home-features-more-link" className="inline-flex items-center gap-2 text-sm font-semibold text-coacher-navy underline decoration-coacher-red decoration-2 underline-offset-8 transition-colors hover:text-coacher-red">
              Explore every feature <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MISSION TEASER */}
      <section data-testid="home-mission-teaser" className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[auto_1fr] lg:gap-20">
            <Reveal>
              <span className="font-varsity text-8xl sm:text-9xl text-outline-navy select-none">01</span>
            </Reveal>
            <div>
              <Reveal delay={0.1}>
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-coacher-red">Our Mission</p>
                <blockquote className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold uppercase leading-[1.08] tracking-tight text-coacher-navy">
                  "Better prepared coaches. Better experiences for players."
                </blockquote>
                <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-coacher-slate">
                  Youth baseball and softball run on volunteers. CoachER exists to give them time back — one practice, one lineup, and one batting order at a time.
                </p>
                <Link to="/mission" data-testid="home-mission-link" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-coacher-navy underline decoration-coacher-red decoration-2 underline-offset-8 transition-colors hover:text-coacher-red">
                  Read our mission <ArrowUpRight size={16} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section data-testid="home-pricing-teaser" className="bg-coacher-surface py-20 sm:py-28 pinstripes">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <SectionHeading
            align="center"
            eyebrow="Pricing"
            title="Simple, honest pricing"
            sub="Free trial with up to 3 players, then one straightforward subscription. No permanent free tier, no games — just less than $5/month on the annual plan."
          />
          <div className="mt-14">
            <PricingCards />
          </div>
        </div>
      </section>

      {/* FINAL CTA + NOTIFY */}
      <section id="launch-notify" data-testid="home-final-cta" className="relative overflow-hidden bg-coacher-navy py-20 sm:py-28">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-white/60">Launching soon on iOS & Android</p>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold uppercase leading-[1.02] tracking-tight text-white">
              Your team needs a coach.<br />
              <span className="text-outline-white">Not more admin.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-white/70">
              CoachER is in app store review now. Join the launch list and we'll email you the moment it's live.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 max-w-2xl mx-auto">
            <NotifyForm />
          </Reveal>
          <Reveal delay={0.25}>
            <StoreButtons className="mt-10 justify-center" />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
